import { Component, Host, HostListener, OnDestroy, OnInit } from "@angular/core";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { TweenMax, gsap, Power1, Power2 } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/all";
gsap.registerPlugin(ScrollToPlugin);
gsap.registerPlugin(ScrollTrigger);

// Setup the ShaderMaterial using the resolved shader code
const shaderMaterial = new THREE.ShaderMaterial({
  wireframe: false,
  uniforms: {
    u_time: { value: 0.0 },
    u_opacity: { value: 1.0 },
    u_color: { value: new THREE.Color(0x000000) },
    u_pointsize: { value: 5.0 },
    u_noise_master_amp: { value: 1.0 },
    u_noise_amp_1: { value: 1.0 },
    u_noise_freq_1: { value: 1.0 },
    u_spd_modifier_1: { value: 1.0 },
    u_noise_amp_2: { value: 1.0 },
    u_noise_freq_2: { value: 1.0 },
    u_spd_modifier_2: { value: 1.0 },
    u_resolution: {
      value: new THREE.Vector2(window.innerWidth, window.innerHeight),
    },
  },
});
interface Settings {
  meshQty: number;
  meshSpacing: number;
  meshSpin: number;
  meshScrollSpin: number;
  meshAutoRotate: {
    x: number;
    y: number;
    z: number;
  };
  camera: {
    origin: THREE.Vector3;
    target: THREE.Vector3;
    mouseSensitivity: {
      x: number;
      y: number;
    };
  };
  color: string;
  opacity: number;
}

interface Uniforms {
  u_time: { value: number };
  u_resolution: { value: { x: number; y: number } };
  u_color: THREE.Color;
  u_noise_master_amp: { value: number };
  u_noise_freq_1: { value: number };
  u_noise_amp_1: { value: number };
  u_spd_modifier_1: { value: number };
  u_noise_freq_2: { value: number };
  u_noise_amp_2: { value: number };
  u_spd_modifier_2: { value: number };
  u_opacity: { value: number };
}

const ie = {
  IS_DEV: window.location.hostname === "localhost",
  IS_DESKTOP: window.innerWidth > 768,
  REDUCED_MOTION: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
};

const Ce = {
  RESIZE_END: "resize",
  READY: "start-animate",
};

class Pe {
  protected el: HTMLElement;

  constructor(el: HTMLElement) {
    this.el = el;
  }

  getData(key: string): string | null {
    return this.el.getAttribute(`data-${key}`);
  }
}

class Ta extends Pe {
  protected sizes: { width: number; height: number } = { width: 0, height: 0 };
  protected scene: THREE.Scene;
  protected camera: THREE.PerspectiveCamera;
  protected renderer: THREE.WebGLRenderer;
  protected clock: THREE.Clock;
  protected isDisabled: boolean = false;
  protected isTicking: boolean = false;
  protected tickerBind?: () => void;
  protected resizeBind?: () => void;
  protected $el: HTMLElement;
  protected geometry?: THREE.BufferGeometry;
  protected material?: THREE.Material;

  constructor(el: HTMLElement) {
    super(el);
    this.$el = el;
  }

  init() {
    if (!ie.REDUCED_MOTION) {
      window.addEventListener(
        Ce.RESIZE_END,
        (this.resizeBind = () => this.resize())
      );
      this.scene = new THREE.Scene();
      this.camera = new THREE.PerspectiveCamera(45, 1);
      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      this.renderer.domElement.setAttribute("aria-hidden", "true");
      this.resize(false);
      if (this.onInit) this.onInit();
      this.clock = new THREE.Clock();
      this.$el.appendChild(this.renderer.domElement);
      this.render();
    }
  }

  start() {
    if (!this.isDisabled && !this.isTicking && !ie.REDUCED_MOTION) {
      this.isTicking = true;
      this.tickerBind = this.render.bind(this);
      gsap.ticker.add(this.tickerBind);
      if (this.onStart) this.onStart();
    }
  }

  stop() {
    if (!this.isDisabled && this.isTicking && !ie.REDUCED_MOTION) {
      this.isTicking = false;
      gsap.ticker.remove(this.tickerBind!);
      if (this.onStop) this.onStop();
    }
  }

  toggle(t: { way?: string }) {
    if (!this.isDisabled && !ie.REDUCED_MOTION) {
      if (
        t.way === "enter" ||
        (typeof t.way == "undefined" && !this.isTicking)
      ) {
        this.start();
      } else {
        this.stop();
      }
      if (this.onToggle) this.onToggle();
    }
  }

  resize(t: boolean = true) {
    if (this.onResize) this.onResize();
    if (!this.isDisabled) {
      this.sizes.width = window.innerWidth;
      this.sizes.height = window.innerHeight;
      this.camera.aspect = this.sizes.width / this.sizes.height;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(this.sizes.width, this.sizes.height);
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      if (!this.isTicking && t) this.render();
    }
  }

  render() {
    if (this.isDisabled) return;
    const delta = this.clock.getDelta();
    const elapsedTime = this.clock.getElapsedTime();
    this.renderer.render(this.scene, this.camera);
    if (this.onRender) this.onRender(delta, elapsedTime);
  }

  destroy() {
    if (!ie.REDUCED_MOTION) {
      this.stop();
      this.geometry?.dispose();
      this.material?.dispose();
      this.renderer.dispose();
      window.removeEventListener(Ce.RESIZE_END, this.resizeBind!);
    }
  }

  // Placeholder methods to be optionally overridden by subclasses
  onInit?(): void;
  onStart?(): void;
  onStop?(): void;
  onToggle?(): void;
  onResize?(): void;
  onRender?(delta: number, elapsedTime: number): void;
}

class Lh extends Ta {
  private SIZE: number;
  private DPR: number;
  private settings: Settings;
  private uniforms!: Uniforms;
  private meshesOuter!: THREE.Group;
  private meshesInner!: THREE.Group;
  private meshes: THREE.Mesh[] = [];
  private introTl!: gsap.core.Timeline;
  private scrollTl!: gsap.core.Timeline;
  private controls?: OrbitControls;

  constructor(el: HTMLElement) {
    super(el);
    this.SIZE = parseFloat(this.getData("size") || "1");
    this.DPR = Math.max(window.devicePixelRatio, 2);
    this.settings = {
      meshQty: 48,
      meshSpacing: 0.02,
      meshSpin: 0.02,
      meshScrollSpin: -Math.PI,
      // meshAutoRotate: { x: 0.0, y: 0.0, z: 0 },
      meshAutoRotate: { x: 0.001, y: 0.001, z: 0 },
      camera: {
        origin: new THREE.Vector3(0, 0, 3),
        target: new THREE.Vector3(0.8, 1, 1.5),
        mouseSensitivity: { x: -0.0001, y: -0.0001 },
      },
      color: document.body.dataset.theme === "light" ? "#000000" : "#ffffff",
      opacity: document.body.dataset.theme === "dark" ? 0.5 : 0.2,
    };
  }

  onInit() {
    this.uniforms = {
      u_time: { value: 0 },
      u_resolution: {
        value: {
          x: this.sizes.width * this.DPR,
          y: this.sizes.height * this.DPR,
        },
      },
      u_color: new THREE.Color(this.settings.color),
      u_noise_master_amp: { value: 0 },
      u_noise_freq_1: { value: 1.17 },
      u_noise_amp_1: { value: 0.09 },
      u_spd_modifier_1: { value: 0.22 },
      u_noise_freq_2: { value: 0.87 },
      u_noise_amp_2: { value: 0.22 },
      u_spd_modifier_2: { value: 0.43 },
      u_opacity: { value: this.settings.opacity },
    };
    this.initCamera();
    this.initObjects();
    this.initScrollTimeline();
    this.handleEvents();
  }

  initCamera() {
    this.camera.position.copy(this.settings.camera.origin);
    const orbit = new THREE.Object3D();
    orbit.rotation.order = "YXZ";
    this.scene.add(orbit);
    orbit.add(this.camera);

    this.camera.lookAt(0, 0, 0);
  }

  initObjects() {
    // const axesHelper = new THREE.AxesHelper(1);
    // axesHelper.visible = true;
    // this.scene.add(axesHelper);
    this.geometry = this.squareGeometry(this.SIZE, 0.001, 0.001);

    this.material = new THREE.MeshStandardMaterial({
      color: this.uniforms.u_color, // Use the color defined in uniforms
      transparent: true,
      opacity: this.uniforms.u_opacity.value,
      metalness: 0.3, // Adds a subtle metallic effect
      roughness: 0.7, // Controls the roughness (can be adjusted for desired effect)
      emissive: this.uniforms.u_color, // Adds a glow effect using the same color
      emissiveIntensity: 0, // Controls the intensity of the emissive effect
    });

    this.meshesOuter = new THREE.Group();
    this.meshesInner = new THREE.Group();
    this.populate();
    this.meshesOuter.add(this.meshesInner);
    this.scene.add(this.meshesOuter);
  }

  squareGeometry(radius: number, thickness: number, depth: number) {
    const shape = new THREE.Shape();
    const r = radius + thickness;
    shape.moveTo(-r, -r);
    shape.lineTo(r, -r);
    shape.lineTo(r, r);
    shape.lineTo(-r, r);
    shape.lineTo(-r, -r);

    const hole = new THREE.Path();
    const hr = radius;
    hole.moveTo(-hr, -hr);
    hole.lineTo(hr, -hr);
    hole.lineTo(hr, hr);
    hole.lineTo(-hr, hr);
    hole.lineTo(-hr, -hr);

    shape.holes.push(hole);

    const extrudeSettings = { steps: 1, depth: depth, bevelEnabled: false };
    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    return geometry;
  }

  populate() {
    this.meshesInner.clear();
    this.meshes = [];
    for (let i = 0; i < this.settings.meshQty; i++) {
      const mesh = new THREE.Mesh(this.geometry!, this.material!);
      this.meshes.push(mesh);
      this.meshesInner.add(mesh);
    }
    this.meshesInner.position.z =
      (-this.meshes.length * this.settings.meshSpacing) / 2;
  }

  initIntroTimeline() {
    if (this.introTl) this.introTl.kill();
    this.introTl = gsap.timeline({
      defaults: { duration: 2, ease: "power3.inOut" },
    });
    this.meshes.forEach((mesh, index) => {
      this.introTl
        .set(mesh, { visible: true }, 0)
        .to(mesh.position, { z: index * this.settings.meshSpacing }, 0)
        .to(mesh.rotation, { z: index * this.settings.meshSpin }, 0);
    });
    this.introTl.to(
      this.uniforms.u_noise_master_amp,
      { value: 1, duration: 4, ease: "power4.inOut" },
      0
    );
  }

  initScrollTimeline() {
    if (this.scrollTl) this.scrollTl.progress(0).kill();
    this.scrollTl = gsap
      .timeline({
        scrollTrigger: {
          trigger: this.$el,
          start: "top top",
          end: "bottom top",
          scrub: 0.5,
        },
        defaults: { duration: 2, ease: "power2.out" },
        paused: true,
        onUpdate: () => {
          this.camera.lookAt(0, 0, 0);
        },
      })
      .fromTo(
        this.camera.position,
        this.settings.camera.origin,
        this.settings.camera.target,
        0
      )
      .to(this.meshesInner.rotation, { z: this.settings.meshScrollSpin }, 0);
  }

  handleEvents() {
    window.addEventListener(Ce.READY, () => this.initIntroTimeline());
    if (ie.IS_DESKTOP) {
      window.addEventListener("mousemove", this.mouseMove.bind(this));
    }
  }

  onRender(delta: number, elapsedTime: number) {
    this.uniforms.u_time.value += delta;
    this.meshesOuter.rotation.x += this.settings.meshAutoRotate.x;
    this.meshesOuter.rotation.y += this.settings.meshAutoRotate.y;
    this.meshesOuter.rotation.z += this.settings.meshAutoRotate.z;
  }

  mouseMove(event: MouseEvent) {
    const orbit = this.camera.parent as THREE.Object3D;
    if (orbit) {
      orbit.rotateY(event.movementX * this.settings.camera.mouseSensitivity.y);
      orbit.rotateX(event.movementY * this.settings.camera.mouseSensitivity.x);
      orbit.rotation.z = 0;
    }
  }

  onScrollProgress(progress: number = 0) {
    if (this.scrollTl) this.scrollTl.progress(progress);
  }

  onResize() {
    this.sizes.width = this.$el.offsetWidth;
    this.sizes.height = this.$el.offsetHeight;
    this.uniforms &&
      ((this.uniforms.u_resolution.value.x = this.sizes.width * this.DPR),
      (this.uniforms.u_resolution.value.y = this.sizes.height * this.DPR));
    this.settings.camera.origin.z = gsap.utils.mapRange(
      500,
      1500,
      3 + 0.5 * this.SIZE,
      3,
      this.sizes.width
    );
  }

  hide() {
    gsap.set(this.$el, { opacity: 0 });
  }

  show() {
    gsap.to(this.$el, { opacity: 1, duration: 1, ease: "power2.out" });
  }

  destroy() {
    super.destroy();
    window.removeEventListener(Ce.READY, this.initIntroTimeline);
    if (ie.IS_DESKTOP) window.removeEventListener("mousemove", this.mouseMove);
  }
}

// Example usage with LocomotiveScroll
// const scroll = new LocomotiveScroll({
//   el: document.querySelector("[data-scroll-container]") as HTMLElement,
//   smooth: true,
// });

// scroll.on("scroll", (event) => {
//   const scrollProgress = event.scroll.y / scroll.scroll.instance.limit.y;
//   const instanceOfLh = new Lh(
//     document.querySelector(".some-element") as HTMLElement
//   );
//   instanceOfLh.onScrollProgress(scrollProgress);
// });

@Component({
  selector: "app-sqanimation",
  templateUrl: "./sqanimation.component.html",
  styleUrls: ["./sqanimation.component.css"],
})
export class SqanimationComponent implements OnInit, OnDestroy {
  animation: Lh;

  ngOnInit(): void {
    this.animation = new Lh(
      document.querySelector("#animate-here") as HTMLElement
    );
    this.animation.init();
    this.animation.start();
    console.log("init")
  }

  // @HostListener("window:scroll", ["$event"])
  // onScroll(event: Event) {
  //   console.log("scrolling", event);
  // }

  ngAfterViewInit() {
    window.dispatchEvent(new Event("start-animate"));
  }
  ngOnDestroy(): void {
    this.animation.destroy();
    console.log("destroy");
  }
}
